<template>
  <Teleport to="body">
    <div
      v-show="visible"
      ref="tipRef"
      class="spd-hover-tooltip"
      :class="{ 'spd-hover-tooltip--chip': kind === 'chip' }"
      role="tooltip"
      :style="tipStyle"
    >
      {{ text }}
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  placeSpdHoverTooltip,
  purpCatChipTooltipTextFromElement,
} from '@/common/utils/spdHoverTooltip'

const SHOW_DELAY_MS = 380
const CHIP_SHOW_DELAY_MS = 80
const GAP_PX = 8

type TipKind = 'title' | 'chip'

const visible = ref(false)
const text = ref('')
const kind = ref<TipKind>('title')
const tipStyle = ref<Record<string, string>>({})
const tipRef = ref<HTMLElement | null>(null)

let showTimer: ReturnType<typeof setTimeout> | null = null
let host: HTMLElement | null = null
let hostKind: TipKind = 'title'
let savedTitle = ''

function findTitleHost(start: EventTarget | null): HTMLElement | null {
  if (!(start instanceof Element)) return null
  const el = start.closest('[title]')
  if (!(el instanceof HTMLElement)) return null
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return null
  if (!el.getAttribute('title')?.trim()) return null
  return el
}

function resolveHoverTooltipHost(
  start: EventTarget | null,
): { el: HTMLElement; kind: TipKind } | null {
  if (!(start instanceof Element)) return null
  const chip = start.closest('.purp-cat-chip')
  if (chip instanceof HTMLElement && purpCatChipTooltipTextFromElement(chip)) {
    return { el: chip, kind: 'chip' }
  }
  const titled = findTitleHost(start)
  if (!titled) return null
  return { el: titled, kind: 'title' }
}

function clearShowTimer() {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
}

function restoreTitle() {
  if (host && hostKind === 'title' && savedTitle) {
    host.setAttribute('title', savedTitle)
  }
  host = null
  hostKind = 'title'
  savedTitle = ''
}

function hide() {
  clearShowTimer()
  visible.value = false
  text.value = ''
  kind.value = 'title'
  restoreTitle()
}

function place() {
  const tip = tipRef.value
  if (!tip || !host) return
  const rect = host.getBoundingClientRect()
  const pos = placeSpdHoverTooltip({
    rect: {
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    },
    tipWidth: tip.offsetWidth,
    tipHeight: tip.offsetHeight,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    gap: GAP_PX,
    placement: host.closest('.sidebar--mini') ? 'side' : 'above',
  })
  tipStyle.value = {
    left: `${pos.left}px`,
    top: `${pos.top}px`,
  }
}

async function showFor(el: HTMLElement, nextKind: TipKind) {
  const nextText =
    nextKind === 'chip'
      ? purpCatChipTooltipTextFromElement(el)
      : el.getAttribute('title')?.trim() || ''
  if (!nextText) return
  host = el
  hostKind = nextKind
  if (nextKind === 'title') {
    savedTitle = nextText
    el.removeAttribute('title')
  }
  text.value = nextText
  kind.value = nextKind
  visible.value = true
  tipStyle.value = { left: '-9999px', top: '0' }
  await nextTick()
  place()
}

function onMouseOver(event: MouseEvent) {
  const target = event.target
  if (host && target instanceof Node && host.contains(target)) return
  const next = resolveHoverTooltipHost(target)
  if (!next) return
  if (next.el === host) return
  const from = resolveHoverTooltipHost(event.relatedTarget)
  if (from?.el === next.el) return
  hide()
  showTimer = setTimeout(() => {
    void showFor(next.el, next.kind)
  }, next.kind === 'chip' ? CHIP_SHOW_DELAY_MS : SHOW_DELAY_MS)
}

function onMouseOut(event: MouseEvent) {
  const related = event.relatedTarget
  if (host) {
    if (related instanceof Node && host.contains(related)) return
    hide()
    return
  }
  const leaving = resolveHoverTooltipHost(event.target)
  if (!leaving) return
  if (related instanceof Node && leaving.el.contains(related)) return
  hide()
}

function onScroll() {
  if (visible.value) hide()
}

onMounted(() => {
  document.addEventListener('mouseover', onMouseOver, true)
  document.addEventListener('mouseout', onMouseOut, true)
  window.addEventListener('scroll', onScroll, true)
})

onBeforeUnmount(() => {
  hide()
  document.removeEventListener('mouseover', onMouseOver, true)
  document.removeEventListener('mouseout', onMouseOut, true)
  window.removeEventListener('scroll', onScroll, true)
})
</script>
